import React from "react";
import { iconSize } from "@/components/ui/icon-container";
import { cn } from "@/utils/utils";
import { UserData } from "@/entities/users/types";
import UserAvatar from "@/components/controls/UserAvatar";
import { useUsers } from "@/entities/users/hooks";
import { DreamerIcon } from "@/components/controls/Questions/DreamerIndication/components/DreamerIcon";
import {
  getOverlapMargin,
  OVERLAP_SHADOW
} from "@/components/controls/Questions/DreamerIndication/components/dreamerIconStyles";

interface StyledUserAvatarProps {
  userData: UserData | undefined;
  size: iconSize;
  isOverlapping?: boolean;
  isDimmed?: boolean;
}

const StyledUserAvatar: React.FC<StyledUserAvatarProps> = ({
  userData,
  size,
  isOverlapping = false,
  isDimmed = false
}) => {
  const avatar = (
    <UserAvatar
      userData={userData}
      size="M"
      className={isDimmed ? "opacity-65" : undefined}
    />
  );

  if (!isOverlapping) {
    return avatar;
  }

  return (
    <div
      className={cn("flex items-center justify-center", getOverlapMargin(size))}
      style={{ boxShadow: OVERLAP_SHADOW }}
    >
      {avatar}
    </div>
  );
};

interface Props {
  dreamerMode: boolean;
  isLastEditedByDreamer: boolean;
  lastEditedByData: UserData | undefined;
  previousLastEditedBy: string | null | undefined;
  size?: iconSize;
}

const DreamerIcons: React.FC<Props> = ({
  dreamerMode,
  isLastEditedByDreamer,
  lastEditedByData,
  previousLastEditedBy,
  size = "sm"
}) => {
  const userIds = previousLastEditedBy ? [previousLastEditedBy] : [];
  const { users } = useUsers(userIds) || { users: [] };
  const [previousLastEditedByData] = users;

  if (dreamerMode) {
    return <DreamerIcon size={size} shouldUseIconContainer />;
  }

  // User edited after Dreamer
  if (!isLastEditedByDreamer) {
    return (
      <>
        <DreamerIcon size={size} isDimmed />
        <StyledUserAvatar
          userData={lastEditedByData}
          size={size}
          isOverlapping
        />
      </>
    );
  }

  // Dreamer edited after User
  return (
    <>
      <StyledUserAvatar
        userData={previousLastEditedByData}
        size={size}
        isDimmed
      />
      <DreamerIcon size={size} isOverlapping />
    </>
  );
};

export default DreamerIcons;
